import * as angular from 'angular';
import * as _ from 'lodash';

import { ComponentDefinition, Group, Identifier, Alarm, AlarmLevel, TimeUnit, IValueViewModel, Dictionary } from '../../../models';
import { IAuthorizationService, IStoreService, IValueTypeHelperService } from '../../../services';
import { WidgetContainerBase } from '../../WidgetContainerBase';
import { IRootScope } from '../../../IRootScope';
import { Store, StoreElement } from '../../../stateManagement';
import { reconciledValues } from '../../../common/constants';

class GroupAlarmsStateController extends WidgetContainerBase implements angular.IComponentController {
    alarms: Alarm[];
    alarmsForm: angular.xeditable.IEditableFormController;
    canEdit: boolean;
    groupGuid: string;
    isLoading: boolean;
    loadingMessage: string;
    selectedGroup: Group;
    selectedIdentifier: Identifier;
    selectedAlarm: Alarm;
    viewModels: Dictionary<IValueViewModel>;

    private formState: { isManual: boolean; level: AlarmLevel; value: string }[] = [];

    static $inject = ['$scope', '$rootScope', '$stateParams', 'authorizationService', 'valueTypeHelperService', 'storeService'];

    constructor(
        private _$scope: angular.IScope,
        private _$rootScope: IRootScope,
        private _$stateParams: angular.ui.IStateParamsService,
        private _authorizationService: IAuthorizationService,
        private _valueTypeHelper: IValueTypeHelperService,
        private _storeService: IStoreService
    ) {
        super(_$rootScope);
    }

    $onInit() {
        this.canEdit = this._authorizationService.canEdit();
        this.isEditMode = this._$rootScope.isEditMode;
        this.groupGuid = this.selectedGroup?.guid;

        this._storeService.register((store) => this.refreshStore(store), [StoreElement.ValueViewModels]);

        this._$scope.$watch(
            () => this.selectedIdentifier,
            (newValue, oldValue) => {
                if (!newValue && oldValue) {
                    this.selectedIdentifier = oldValue;
                }
                if (oldValue !== newValue) {
                    this.formState = [];
                }
            }
        );

        if (this.selectedGroup && this.selectedGroup.identifierCollection.length > 0) {
            if (this._$stateParams['identifierGuid']) {
                const found = _.find(this.selectedGroup.identifierCollection, (id) => id.guid === this._$stateParams['identifierGuid']);
                if (found) {
                    this.selectedIdentifier = found;
                }
            } else {
                this.selectedIdentifier = _.sortBy(this.selectedGroup.identifierCollection, ['sortingOrder', 'name'])[0];
            }
        }
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['selectedGroup']) {
            if (this.selectedGroup) {
                this.groupGuid = this.selectedGroup.guid;
            }
        }
    }

    private refreshStore(store: Store) {
        this.viewModels = store.valueViewModels.dictionary;
    }

    cancel(): void {
        this.alarmsForm.$cancel();
        this.alarmsForm.$setPristine();
        this.formState = undefined;
    }

    submit(): void {
        this.alarmsForm.$submit();
    }

    add(): void {
        const type = this.selectedIdentifier.type === undefined ? (this.selectedIdentifier as any).identifierType : this.selectedIdentifier.type;
        const vm = this.viewModels[this.selectedIdentifier.guid];
        const valueTypes = !!vm ? this._valueTypeHelper.getViewModelValueTypes(vm) : this._valueTypeHelper.getIdentifierValueTypes(type);
        const valueType = valueTypes?.length > 0 ? valueTypes[0].key : '';
        const defaultValueType = vm && vm.hasReconciledValue ? reconciledValues[0].key : valueType;
        const newAlarm: Alarm = {
            categories: [],
            comment: '',
            consequenceOfFailure: '',
            delay: { interval: 0, intervalTimeUnit: TimeUnit.Second },
            guid: undefined,
            isDisabled: false,
            isManual: false,
            level: AlarmLevel.Information,
            mode: 0,
            probabilityOfFailure: '',
            sortingOrder: 0,
            states: [],
            value: defaultValueType,
            weight: 1
        };

        this.initFormState();
        this.alarms = [...this.alarms].concat([newAlarm]);
        this.formState = [...this.formState].concat([{ ...newAlarm, isManual: !!newAlarm.isManual }]);
        this.selectedAlarm = newAlarm;
        this.alarmsForm.$setDirty();
        this.invalidateForm();
    }

    onDelete(index: number) {
        if (this.formState && index >= 0) {
            const newState = this.formState.map((d) => d);
            newState.splice(index, 1);
            this.formState = newState;
        }
    }

    showForm() {
        this.alarmsForm.$show();
        this.initFormState();
    }

    initFormState() {
        if (this.alarmsForm?.$visible && this.alarms?.length && !this.formState) {
            this.formState = this.alarms.map((alarm) => {
                const a = { ...alarm, isManual: !!alarm.isManual };
                if (a.isManual) {
                    a.low = undefined;
                    a.high = undefined;
                }
                return a;
            });
        }
    }

    updateFormState(index: number, newValues: { isManual?: boolean; level?: AlarmLevel; value?: string }) {
        this.initFormState();
        this.formState = this.formState.map((d, i) => {
            let retVal = { ...d };
            if (i === index) {
                retVal = { ...retVal, ...newValues };
            }
            return retVal;
        });
        this.invalidateForm();
    }

    private invalidateForm() {
        let message: string | undefined = undefined;
        if (this.alarms && this.alarmsForm) {
            this.alarms?.forEach((a, index) => {
                this.alarmsForm.$setValidity('duplicates', true, this.alarmsForm[`isManual${index}`]);
            });
            this.alarmsForm.$error = {};
            const byKey = _.groupBy(this.formState, (a) => JSON.stringify({ value: a.value, level: a.level }));
            const mismatch = Object.values(byKey).filter((d) => {
                const manuals = d.filter((a) => a.isManual);
                return manuals.length > 1 || (manuals.length === 1 && _.some(d, (a) => !a.isManual));
            });
            if (mismatch.length > 0) {
                const dup = mismatch
                    .flatMap((d) => d)
                    .flatMap((mm) => {
                        return this.alarms.filter((a) => a.level === mm.level && a.value === mm.value).map((d) => this.alarms.indexOf(d));
                    });
                dup.forEach((d) => {
                    this.alarmsForm.$setValidity('duplicates', false, this.alarmsForm[`isManual${d}`]);
                });
                message = 'Invalid configuration';
            }
        }
        return message;
    }
}

export const wtGroupAlarmsState: ComponentDefinition = {
    componentId: 'wtGroupAlarmsState',
    controller: GroupAlarmsStateController,
    template: require('./groupAlarmsState.component.html'),
    controllerAs: 'vm',
    bindings: {
        selectedGroup: '<',
        selectedIdentifier: '=',
        selectedAlarm: '=?',
        canEdit: '<'
    }
};
